// First-touch, session-scoped visit attribution (non-PII).
// Captured once per tab session so it survives in-session navigation, then
// stamped onto conversion events. Stores only campaign params, the referrer
// HOSTNAME (never the full URL), and the landing path.

const STORAGE_KEY = 'hzn_visit_attribution';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export type VisitAttribution = {
  landing_path?: string;
  referrer_host?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

function readReferrerHost(): string {
  try {
    if (!document.referrer) return '';
    const host = new URL(document.referrer).hostname;
    // Ignore same-host (internal) referrers — we want the external entry source.
    if (host === window.location.hostname) return '';
    return host;
  } catch {
    return '';
  }
}

// Capture first-touch attribution once per session. No-op if already stored,
// if run without a DOM (SSR), or if sessionStorage is unavailable.
export function captureVisitAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;

    const params = new URL(window.location.href).searchParams;
    const attribution: VisitAttribution = {
      landing_path: window.location.pathname,
    };

    const referrerHost = readReferrerHost();
    if (referrerHost) attribution.referrer_host = referrerHost;

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) attribution[key] = value;
    }

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage can throw (private mode, quota) — attribution is best-effort.
  }
}

// Read stored attribution; returns {} if absent or unreadable.
export function getVisitAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}
